import React from "react";

/**
 * WorldFlags - strip of export-destination flags rendered as proper SVGs.
 *
 * Sized identically to the Indian flag in the navbar (5:3 aspect ratio,
 * width 28–40px clamped). Each flag is hand-drawn in SVG so it scales
 * crisply and has correct national colours / proportions.
 *
 * Animation: a gentle wave runs across all flags with a small per-flag
 * delay (0.18s stagger) so the strip ripples once rather than every
 * flag bouncing independently.
 */

const COUNTRIES = [
  { code: "AE", name: "United Arab Emirates", Flag: AE },
  { code: "SA", name: "Saudi Arabia",         Flag: SA },
  { code: "QA", name: "Qatar",                Flag: QA },
  { code: "OM", name: "Oman",                 Flag: OM },
  { code: "GB", name: "United Kingdom",       Flag: GB },
  { code: "DE", name: "Germany",              Flag: DE },
  { code: "NL", name: "Netherlands",          Flag: NL },
  { code: "US", name: "United States",        Flag: US },
  { code: "CA", name: "Canada",               Flag: CA },
  { code: "AU", name: "Australia",            Flag: AU },
  { code: "BR", name: "Brazil",               Flag: BR },
];

export default function WorldFlags() {
  // Render the full list twice. On mobile a CSS marquee translates the
  // track by -50%, so the second copy slides in seamlessly as the first
  // exits - infinite loop with no visible seam. On desktop the marquee
  // animation is off and the second copy simply sits next to the first
  // (clipped naturally because all 11 flags fit in the available width
  // on most desktops; if they don't, native horizontal scroll still works).
  const renderRow = (keyPrefix, ariaHidden = false) =>
    COUNTRIES.map((c, i) => (
      <span
        key={`${keyPrefix}-${c.code}`}
        className="world-flag"
        role={ariaHidden ? undefined : "listitem"}
        aria-label={ariaHidden ? undefined : c.name}
        aria-hidden={ariaHidden || undefined}
        title={ariaHidden ? undefined : c.name}
        style={{ animationDelay: `${i * 0.18}s` }}
      >
        <c.Flag />
      </span>
    ));

  return (
    <div className="world-flags-strip" role="list" aria-label="Active export destinations">
      <div className="world-flags-track">
        {renderRow("a", false)}
        {renderRow("b", true)}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Each flag uses a 100×60 viewBox (5:3) - close to the official 3:2
   for most national flags and matches the navbar Indian flag. Drawn
   strictly with correct national colours from each spec.
   ──────────────────────────────────────────────────────────────────── */

const Svg = ({ children }) => (
  <svg viewBox="0 0 100 60" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
    {children}
  </svg>
);

/** UAE - vertical red hoist (1/4) + horizontal green/white/black (3/4). */
function AE() {
  return (
    <Svg>
      <rect x="0"  y="0"  width="25" height="60" fill="#EF3340" />
      <rect x="25" y="0"  width="75" height="20" fill="#009639" />
      <rect x="25" y="20" width="75" height="20" fill="#FFFFFF" />
      <rect x="25" y="40" width="75" height="20" fill="#000000" />
    </Svg>
  );
}

/** Saudi Arabia - green field with shahada + sword. Stylised at small scale. */
function SA() {
  return (
    <Svg>
      <rect x="0" y="0" width="100" height="60" fill="#006C35" />
      {/* Shahada line - abstracted as a thin white band of glyph-like marks */}
      <g fill="#FFFFFF">
        <rect x="22" y="22" width="56" height="2" />
        <rect x="22" y="26" width="56" height="2" />
        <rect x="22" y="30" width="56" height="1.6" rx="0.3" />
      </g>
      {/* Sword */}
      <g fill="#FFFFFF" stroke="#FFFFFF" strokeWidth="0.4">
        <rect x="22" y="38" width="56" height="1.6" />
        <polygon points="22,38.8 18,38.8 22,40.4" />
        <rect x="76" y="36" width="2" height="5.6" />
      </g>
    </Svg>
  );
}

/** Qatar - maroon/white with 9-point serrated boundary. */
function QA() {
  // 9 serrations between hoist white band and maroon field.
  // White band on left = ~28% of width. Serrated edge zigzag.
  const PEAKS = 9;
  const LEFT = 22;   // start of zigzag
  const RIGHT = 38;  // peak point of zigzag
  const points = [];
  points.push(`0,0 ${LEFT},0`);
  for (let i = 0; i < PEAKS; i++) {
    const yTop = (i * 60) / PEAKS;
    const yMid = ((i + 0.5) * 60) / PEAKS;
    const yBot = ((i + 1) * 60) / PEAKS;
    points.push(`${RIGHT},${yMid}`);
    if (i < PEAKS - 1) points.push(`${LEFT},${yBot}`);
  }
  points.push(`${LEFT},60 0,60`);
  return (
    <Svg>
      <rect x="0" y="0" width="100" height="60" fill="#8A1538" />
      <polygon points={points.join(" ")} fill="#FFFFFF" />
    </Svg>
  );
}

/** Oman - red hoist + horizontal white/red/green with national emblem hint. */
function OM() {
  return (
    <Svg>
      <rect x="0"  y="0"  width="100" height="20" fill="#FFFFFF" />
      <rect x="0"  y="20" width="100" height="20" fill="#DB161B" />
      <rect x="0"  y="40" width="100" height="20" fill="#008000" />
      <rect x="0"  y="0"  width="22"  height="60" fill="#DB161B" />
      {/* Emblem - simplified khanjar dagger + crossed swords */}
      <g stroke="#FFFFFF" strokeWidth="1.2" fill="none">
        <line x1="11" y1="14" x2="11" y2="26" />
        <line x1="6"  y1="20" x2="16" y2="20" />
        <line x1="7"  y1="16" x2="15" y2="24" />
        <line x1="15" y1="16" x2="7"  y2="24" />
      </g>
    </Svg>
  );
}

/** United Kingdom - Union Jack. Diagonal saltires + central cross. */
function GB() {
  return (
    <Svg>
      <defs>
        <clipPath id="gb-clip">
          <rect x="0" y="0" width="100" height="60" />
        </clipPath>
      </defs>
      <g clipPath="url(#gb-clip)">
        {/* Blue field */}
        <rect width="100" height="60" fill="#012169" />
        {/* White diagonals */}
        <line x1="0"   y1="0"  x2="100" y2="60" stroke="#FFFFFF" strokeWidth="12" />
        <line x1="100" y1="0"  x2="0"   y2="60" stroke="#FFFFFF" strokeWidth="12" />
        {/* Red diagonals (St Patrick's saltire) - offset for proper Union Jack */}
        <line x1="0"   y1="0"  x2="100" y2="60" stroke="#C8102E" strokeWidth="4" />
        <line x1="100" y1="0"  x2="0"   y2="60" stroke="#C8102E" strokeWidth="4" />
        {/* White cross (St George) */}
        <rect x="0"   y="22" width="100" height="16" fill="#FFFFFF" />
        <rect x="42"  y="0"  width="16"  height="60" fill="#FFFFFF" />
        {/* Red cross */}
        <rect x="0"   y="26" width="100" height="8"  fill="#C8102E" />
        <rect x="46"  y="0"  width="8"   height="60" fill="#C8102E" />
      </g>
    </Svg>
  );
}

/** Germany - three horizontal bands: black, red, gold. */
function DE() {
  return (
    <Svg>
      <rect x="0" y="0"  width="100" height="20" fill="#000000" />
      <rect x="0" y="20" width="100" height="20" fill="#DD0000" />
      <rect x="0" y="40" width="100" height="20" fill="#FFCE00" />
    </Svg>
  );
}

/** Netherlands - three horizontal bands: red, white, blue. */
function NL() {
  return (
    <Svg>
      <rect x="0" y="0"  width="100" height="20" fill="#AE1C28" />
      <rect x="0" y="20" width="100" height="20" fill="#FFFFFF" />
      <rect x="0" y="40" width="100" height="20" fill="#21468B" />
    </Svg>
  );
}

/** USA - 13 stripes + 50 stars (5×6+4×5 = 50 in canton). */
function US() {
  // 13 horizontal stripes, height 60/13 ≈ 4.615 each, alternating red/white.
  const STRIPE_H = 60 / 13;
  const stripes = [];
  for (let i = 0; i < 13; i++) {
    stripes.push(
      <rect
        key={`s-${i}`}
        x="0"
        y={i * STRIPE_H}
        width="100"
        height={STRIPE_H}
        fill={i % 2 === 0 ? "#B22234" : "#FFFFFF"}
      />
    );
  }
  // Canton: top-left, 7 stripes tall, 40% wide.
  const CANTON_W = 40;
  const CANTON_H = STRIPE_H * 7;
  // 50 stars: 9 rows alternating 6 and 5 stars (6+5+6+5+6+5+6+5+6 = 50).
  const stars = [];
  const rows = 9;
  for (let r = 0; r < rows; r++) {
    const isLong = r % 2 === 0;
    const count = isLong ? 6 : 5;
    const cy = ((r + 1) / (rows + 1)) * CANTON_H;
    for (let c = 0; c < count; c++) {
      const cx = isLong
        ? ((c + 1) / (count + 1)) * CANTON_W
        : ((c + 1) / (count + 1)) * CANTON_W + (CANTON_W / (count + 1)) / 2 - 0.4;
      stars.push(<circle key={`star-${r}-${c}`} cx={cx} cy={cy} r="0.65" fill="#FFFFFF" />);
    }
  }
  return (
    <Svg>
      {stripes}
      <rect x="0" y="0" width={CANTON_W} height={CANTON_H} fill="#3C3B6E" />
      {stars}
    </Svg>
  );
}

/** Canada - vertical red bars on either side, white middle, red maple leaf. */
function CA() {
  // 11-pointed maple leaf (simplified). Canada flag spec uses a stylised
  // leaf - drawn here with a recognisable polygon at appropriate scale.
  return (
    <Svg>
      <rect x="0"  y="0" width="25" height="60" fill="#FF0000" />
      <rect x="25" y="0" width="50" height="60" fill="#FFFFFF" />
      <rect x="75" y="0" width="25" height="60" fill="#FF0000" />
      <path
        d="M50,12
           L52,20 L58,18 L55,25 L62,26 L57,30
           L60,35 L54,34 L52,40 L50,46
           L48,40 L46,34 L40,35 L43,30
           L38,26 L45,25 L42,18 L48,20 Z"
        fill="#FF0000"
      />
    </Svg>
  );
}

/** Australia - blue field + Union-Jack canton + Commonwealth Star + Southern Cross. */
function AU() {
  // Canton size: half width × half height.
  return (
    <Svg>
      <rect x="0" y="0" width="100" height="60" fill="#012169" />
      {/* Canton - simplified Union Jack */}
      <g>
        <rect x="0" y="0" width="50" height="30" fill="#012169" />
        <line x1="0"  y1="0"  x2="50" y2="30" stroke="#FFFFFF" strokeWidth="6" />
        <line x1="50" y1="0"  x2="0"  y2="30" stroke="#FFFFFF" strokeWidth="6" />
        <line x1="0"  y1="0"  x2="50" y2="30" stroke="#C8102E" strokeWidth="2" />
        <line x1="50" y1="0"  x2="0"  y2="30" stroke="#C8102E" strokeWidth="2" />
        <rect x="0"  y="11" width="50" height="8" fill="#FFFFFF" />
        <rect x="21" y="0"  width="8"  height="30" fill="#FFFFFF" />
        <rect x="0"  y="13" width="50" height="4" fill="#C8102E" />
        <rect x="23" y="0"  width="4"  height="30" fill="#C8102E" />
      </g>
      {/* Commonwealth Star - 7 points, below canton */}
      <circle cx="25" cy="44" r="3" fill="#FFFFFF" />
      {/* Southern Cross - 5 stars on the right */}
      <circle cx="78" cy="14" r="1.6" fill="#FFFFFF" />
      <circle cx="86" cy="22" r="1.4" fill="#FFFFFF" />
      <circle cx="74" cy="30" r="1.2" fill="#FFFFFF" />
      <circle cx="82" cy="40" r="1.6" fill="#FFFFFF" />
      <circle cx="78" cy="48" r="0.9" fill="#FFFFFF" />
    </Svg>
  );
}

/** Brazil - green field, yellow diamond, blue globe with stars (simplified). */
function BR() {
  return (
    <Svg>
      <rect x="0" y="0" width="100" height="60" fill="#009C3B" />
      <polygon points="50,7 93,30 50,53 7,30" fill="#FFDF00" />
      <circle cx="50" cy="30" r="13" fill="#002776" />
      {/* "Ordem e Progresso" band - abstracted thin white arc */}
      <path d="M37,32 Q50,26 63,32" stroke="#FFFFFF" strokeWidth="1.3" fill="none" />
      {/* A few stars */}
      <g fill="#FFFFFF">
        <circle cx="44" cy="28" r="0.7" />
        <circle cx="50" cy="26" r="0.8" />
        <circle cx="56" cy="28" r="0.7" />
        <circle cx="48" cy="34" r="0.6" />
        <circle cx="54" cy="35" r="0.6" />
      </g>
    </Svg>
  );
}
